import { memo } from 'react';
import { Box, Card, CloseButton, Text, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { useIsInMobileWebView } from '../../hooks';

type EmptyCardProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  link: string;
  buttonText: string;
  onClose?: () => void;
  buttonAriaLabel?: string;
  dataTestId: string;
  shouldCopyLink?: boolean;
};

const CONTENT = {
  TOAST_SUCCESS: 'Link copied',
  TOAST_ERROR: 'Unable to copy link. Please try again.',
} as const;

const EmptyCard = memo(
  ({
    icon,
    title,
    description,
    link,
    buttonText,
    onClose,
    buttonAriaLabel,
    dataTestId,
    shouldCopyLink,
  }: EmptyCardProps) => {
    const isInMobileWebView = useIsInMobileWebView();
    const { enqueueToast } = useToast();

    const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (shouldCopyLink) {
        e.preventDefault();
        try {
          await navigator.clipboard.writeText(link);
          enqueueToast(
            <Toast id="copy-link-toast-success" tone="success">
              {CONTENT.TOAST_SUCCESS}
            </Toast>,
          );
        } catch {
          enqueueToast(
            <Toast id="copy-link-toast-error" tone="critical">
              {CONTENT.TOAST_ERROR}
            </Toast>,
          );
        }
      }
    };

    return (
      <Card
        elevated={false}
        data-testid={`DocumentCard-${dataTestId}`}
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '{spacing.default} {spacing.loose} {spacing.loose}',
          flex: 1,
          height: '100%',
        }}
      >
        <Box css={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          {icon}
          {onClose && <CloseButton onClick={onClose} />}
        </Box>
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 'tighter',
            padding: '{spacing.looser} 0 {spacing.default}',
            flex: '1 1 auto',
          }}
        >
          <Text textStyle="heading-xs">{title}</Text>
          <Text>{description}</Text>
        </Box>
        <Box css={{ marginTop: 'auto' }}>
          <TextButton asChild>
            <a
              href={link}
              aria-label={buttonAriaLabel || buttonText}
              onClick={handleClick}
              {...(!isInMobileWebView && { target: '_blank', rel: 'noreferrer' })}
            >
              {buttonText}
            </a>
          </TextButton>
        </Box>
      </Card>
    );
  },
);

EmptyCard.displayName = 'EmptyCard';

export default EmptyCard;
